import { useState } from "react";
import { ChevronDownIcon } from "lucide-react";

import { PipelineEntryChip } from "@jobs/pipeline/shared/PipelineEntryChip";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  PausedJobMoveDecision,
  usePausedJobMoveGate,
} from "@/contexts/PausedJobMoveGateContext";
import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";

import {
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
  getSourcedType,
  sourcedTypeToDotColor,
  sourcedTypeToLabel,
  stageChipStatusesByStage,
  STATUS_CHANGE_DIALOG_STATUSES,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { stevieRoute } from "@/lib/stevieRoute";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { CandidacyStatus, MinimalCandidacy, PipelineStage } from "@/types";

export const CandidacyStageChip = ({
  candidacy,
  stage,
  disabled = false,
  onRequestStatusChangeDialog,
}: {
  candidacy: MinimalCandidacy;
  stage: PipelineStage;
  disabled?: boolean;
  onRequestStatusChangeDialog: (
    candidacyId: string,
    status: CandidacyStatus,
  ) => void;
}) => {
  const [open, setOpen] = useState(false);
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const { mutate: updateCandidacy, isPending } = useUpdateCandidacy();
  const pausedJobMoveGate = usePausedJobMoveGate();
  const currentJobId = useCurrentJobId();

  const options = stageChipStatusesByStage[stage];
  const currentStatus = candidacy.lastCandidacyStatusChange.toStatus;

  /* Handlers */
  const isOptionDisabled = (option: CandidacyStatus) => {
    if (option === currentStatus) return true;
    if (isPending) return true;
    if (currentStatus === CandidacyStatus.PLACED) {
      // Allow moving forward to employed or rolling back to offer/interview.
      return (
        option !== CandidacyStatus.EMPLOYED &&
        option !== CandidacyStatus.OFFER &&
        option !== CandidacyStatus.INTERVIEW
      );
    }
    if (currentStatus === CandidacyStatus.EMPLOYED) {
      return true;
    }
    return false;
  };

  const handleStatusChange = async (newStatus: CandidacyStatus) => {
    setOpen(false);
    if (newStatus === currentStatus) return;

    // Ahead of the interstitial branch below, so a parked job asks to be
    // reactivated before any Schedule Screen / Interview form opens.
    const decision = await pausedJobMoveGate?.requestDecision({
      candidacyId: candidacy.id,
      fromStatus: currentStatus,
      jobId: currentJobId,
      toStatus: newStatus,
    });
    if (decision === PausedJobMoveDecision.CANCEL) return;

    mixpanelUtils.trackButtonClick("Status Changed", MIXPANEL_SCREENS.JOB, {
      from_status: currentStatus,
      to_status: newStatus,
      source: "kanban_stage_chip",
    });

    if (STATUS_CHANGE_DIALOG_STATUSES.has(newStatus)) {
      onRequestStatusChangeDialog(candidacy.id, newStatus);
      return;
    }

    updateCandidacy(
      {
        candidacyId: candidacy.id,
        updateCandidacyRequest: {
          fromStatus: currentStatus,
          status: newStatus,
        },
      },
      {
        onSuccess: () => {
          setCandidacyInAllLists({
            candidacyId: candidacy.id,
            lastCandidacyStatusChange: {
              createdAt: new Date().toISOString(),
              fromStatus: currentStatus,
              toStatus: newStatus,
              statusChangeReason: null,
              byAgent: false,
            },
          });
        },
      },
    );
  };

  /* Render */
  const render = () => {
    // In the Applied stage, a sourced or resurrected candidacy shows how it
    // entered the pipeline in place of the status chip.
    if (stage === PipelineStage.APPLIED) {
      const sourcedType = getSourcedType(
        candidacy.source,
        candidacy.isExternallySourced,
        candidacy.isAutoResurrected,
        stevieRoute.isBackFromCall(candidacy),
      );

      return (
        <PipelineEntryChip
          dotClassName={
            sourcedType
              ? sourcedTypeToDotColor[sourcedType]
              : candidacyStatusToBackgroundColor[stage]
          }
          label={
            sourcedType
              ? sourcedTypeToLabel[sourcedType]
              : candidacyStatusToLabel[stage]
          }
        />
      );
    }

    if (!options) return null;
    // Status doesn't fall in this stage's options — don't guess what to show.
    if (!options.includes(currentStatus)) return null;

    return (
      <DropdownMenu onOpenChange={setOpen} open={open && !disabled}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            className={cn(
              "h-6 gap-1.5 rounded-full border border-slate-300 bg-white px-2 py-0 text-[11px] font-medium text-slate-800 shadow-sm hover:border-slate-400 hover:bg-slate-50",
              disabled && "opacity-60 hover:border-slate-300 hover:bg-white",
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
            variant="ghost"
          >
            <span
              aria-hidden
              className={cn(
                "h-2 w-2 shrink-0 rounded-full",
                candidacyStatusToBackgroundColor[currentStatus],
              )}
            />
            <span className="truncate">
              {candidacyStatusToLabel[currentStatus]}
            </span>
            <ChevronDownIcon className="h-3 w-3 shrink-0 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-48"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {options.map((option) => (
            <DropdownMenuItem
              className="flex items-center gap-2"
              disabled={isOptionDisabled(option)}
              key={option}
              onClick={() => {
                void handleStatusChange(option);
              }}
            >
              <span
                aria-hidden
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  candidacyStatusToBackgroundColor[option],
                )}
              />
              {candidacyStatusToLabel[option]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return render();
};
